import * as CubiqTS from "shared/cubiq-model-multi-asset";

// adapter imports
import { Injectable } from "@angular/core";
import { CcyLookupService } from "../../lookup/model/impl/CcyLookupService";
import { CurrencyPairCalendarLookupService } from "../../lookup/model/impl/CurrencyPairCalendarLookupService";

import { LoggerService } from "@bfx/logger";
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const logger = LoggerService.for(__dirname);

import { BMOrderAmountHelper } from "shared/cubiq-services/model/benchmarks/helpers/BMOrderAmountHelper";
import { FixingDateSCHelper } from "shared/cubiq-services/model/orders/FixingDateSCHelper";
import { ValueDateHelper } from "shared/cubiq-services/model/orders/ValueDateHelper";
import {
  COLUMN_DESK_ID,
  COLUMN_SALES_GROUP_ID,
} from "shared/cubiq-services/model/OrdersTableDefinitionFactory.constants";
import { COLUMN_ENTRY_USER_ID } from "shared/cubiq-services/model/TradeTableDefinitionFactory.constants";
import { ScratchPadOrder } from "./ScratchPadOrder";

const MAX_TOOLTIP_SIZE: number = 512;

@Injectable()
export class ScratchPadOrderFactoryService {
  private orderAmountHelper: BMOrderAmountHelper;
  private valueDateHelper: ValueDateHelper;
  private bmFixingDateHelper: FixingDateSCHelper;

  constructor(
    protected ccyLookupService: CcyLookupService,
    protected ccyPairCalSvc: CurrencyPairCalendarLookupService,
  ) {
    this.orderAmountHelper = new BMOrderAmountHelper(ccyLookupService);
    this.valueDateHelper = new ValueDateHelper(ccyPairCalSvc);

    this.bmFixingDateHelper = new FixingDateSCHelper();
  }

  public createScratchPadOrder(
    topRequest: CubiqTS.LinkedOrderRequestImpl | CubiqTS.FixingOrderRequestDataImpl,
  ): ScratchPadOrder {
    const retOrder = new ScratchPadOrder();

    // this.mapPropertiesByType(retOrder, topRequest);

    if (topRequest instanceof CubiqTS.FixingOrderRequestImpl && topRequest.fixingOrderData) {
      const orderRequest: CubiqTS.FixingOrderRequestData = topRequest.fixingOrderData;
      this.initialiseCommonProperties(retOrder, topRequest.extendedInfo, orderRequest);
      this.populateFromBenchmarkRequest(retOrder, topRequest);
    } else if (topRequest instanceof CubiqTS.LinkedOrderRequestImpl) {
      let orderRequest: CubiqTS.OrderRequestDataImpl = topRequest.rootOrder;
      this.initialiseCommonProperties(retOrder, topRequest.extendedInfo, orderRequest);
      this.populateFromLimitRequest(retOrder, topRequest);
    }

    //REMOVE ADDITIONAL INFO ADDED IN THE LOOKUP SERVICE
    if (topRequest.extendedInfo) {
      topRequest.extendedInfo.delete(COLUMN_SALES_GROUP_ID);
      topRequest.extendedInfo.delete(COLUMN_DESK_ID);
      topRequest.extendedInfo.delete(COLUMN_ENTRY_USER_ID);
      topRequest.extendedInfo.delete("oldClOrdId");
      topRequest.extendedInfo.delete("oldSiblingRefId");
      topRequest.extendedInfo.delete("errors");
    }
    return retOrder;
  }

  private initialiseCommonProperties(
    order: ScratchPadOrder,
    extendedInfo: Map<string, string>,
    orderRequest: CubiqTS.OrderRequestDataImpl | CubiqTS.FixingOrderRequestDataImpl,
  ) {
    if (orderRequest) {
      order.orderId = orderRequest.orderId ? orderRequest.orderId : null;
      order.orderRef = orderRequest.orderRef ? orderRequest.orderRef : null;
      order.rootOrder = orderRequest;
      order.key = orderRequest.orderId;
      order.base = orderRequest.base;
      order.nonBase = orderRequest.nonBase;
      order.orderState = orderRequest["orderState"] ? orderRequest["orderState"] : null;
      order.clientCode = orderRequest.clientCode;
      order.accountCode = orderRequest.accountCode;
      order.product = orderRequest.product;
      order.instrument = orderRequest.base + "/" + orderRequest.nonBase;
      order.requestOrderType = orderRequest.requestOrderType;
      order.orderType = orderRequest.orderType;
      order.markup = orderRequest.markup;
      order.markupType = orderRequest.markup ? orderRequest.markupType : null;
      order.fixingSourceCd = orderRequest.fixingSource;
      order.fixingDate = orderRequest.fixingDate;
      order.fixingDateMoment = this.bmFixingDateHelper.getDate(order, "fixingDate");

      order.traderGroup = orderRequest.traderGroup;
      order.comments1 = orderRequest.comment;
      order.comments2 = orderRequest.comment2;
      order.externalClientReference = orderRequest.externalClientReference;
      order.oboClient = orderRequest.oboClient;
      order.oboAccount = orderRequest.oboAccount;
      order.customValues = orderRequest.customValues;

      order.deskId = extendedInfo.get(COLUMN_DESK_ID) ?? null;
      order.salesGroupId = extendedInfo.get(COLUMN_SALES_GROUP_ID) ?? null;
      order.entryUserId = extendedInfo.get(COLUMN_ENTRY_USER_ID) ?? null;
      order.oldClOrdId = extendedInfo.get("oldClOrdId") ?? null;
      order.oldSiblingRefId = extendedInfo.get("oldSiblingRefId") ?? null;
      order.errors = extendedInfo.get("errors") ?? null;

      // Set up default values that will be changed for multi-allocation Swaps and SSPs
      order.accountCode = orderRequest.accountCode;
      order.oboAccount = orderRequest.oboAccount;
      order.oboClient = orderRequest.oboClient;
      order.clientInt = orderRequest.clientIntent;
      order.fixedCcy = orderRequest.fixedCcy;
      order.fixedAmount = orderRequest.fixedAmount;
      order.valueDate = orderRequest.valueDate;
      order.tenor = orderRequest.tenor;
      order.channelCd = orderRequest.tradingChannel;
      order.multiAllocation = false;
    }
  }

  protected populateFromLimitRequest(
    order: ScratchPadOrder,
    topRequest: CubiqTS.LinkedOrderRequestImpl,
  ) {
    if (topRequest instanceof CubiqTS.LinkedOrderRequestImpl) {
      let orderRequest: CubiqTS.OrderRequestDataImpl = topRequest.rootOrder;
      if (orderRequest) {
        order.activationDetails = orderRequest.activationDetails
          ? orderRequest.activationDetails
          : null;
        order.expiryDetails = orderRequest.expiryDetails ? orderRequest.expiryDetails : null;

        order.fillTypeRestriction = orderRequest.fillTypeRestriction;
        order.siblingId =
          orderRequest.sibling != null && orderRequest.sibling.order != null
            ? orderRequest.sibling.order.externalClientReference
            : null;
        order.siblingOrderId =
          orderRequest.sibling != null &&
          orderRequest.sibling.order != null &&
          orderRequest.sibling.order.orderId < orderRequest.orderId
            ? orderRequest.sibling.order.orderId
            : null;
        order.stpHold = orderRequest.stpHold;
        order.fillTypeRestriction = orderRequest.fillTypeRestriction;
        order.isPrivateOrder = orderRequest.isPrivateOrder;
        order.fillSupport = orderRequest.fillSupport;

        order.clTrader = topRequest.clientTrader;
        order.sibling = orderRequest.sibling != null ? orderRequest.sibling : null;
        order.childRelationships =
          orderRequest.childRelationships != null ? orderRequest.childRelationships : null;
        order.requestRate = orderRequest.requestRate;
        order.forwardTrackingType = orderRequest.forwardTrackingType;
        order.tslStartRate = orderRequest.tslStartRate;
        order.tslDistancePips = orderRequest.tslDistancePips;
      }
    }
  }

  private populateFromBenchmarkRequest(
    order: ScratchPadOrder,
    topRequest: CubiqTS.FixingOrderRequestImpl,
  ) {
    let orderRequest: CubiqTS.FixingOrderRequestData = topRequest.fixingOrderData;

    if (orderRequest) {
      order.fixingTime = orderRequest.fixingTime != null ? orderRequest.fixingTime : null;
      // helper uses the fixing time and previosly init fixingDate
      order.bmFixingDateLabel = this.bmFixingDateHelper.retrieveData(order, "fixingDate");
      order.fixingDateMoment = this.bmFixingDateHelper.getDate(order, "fixingDate");
      order.bmFixingTime = this.bmFixingDateHelper.getTimeAndZone(order);

      order.stpHold = topRequest.stpHold;
      order.isPrivateOrder = topRequest.privateFg;
      order.clTrader = topRequest.clientTrader;
      order.fwdNettingId = topRequest.fixingOrderData.fwdNettingId;

      let sortedAllocations = this.sortAllocations(orderRequest.allocations);
      const nearAllocations = (orderRequest.allocations = sortedAllocations);
      sortedAllocations = this.sortAllocations(orderRequest.farAllocations);
      const farAllocations = (orderRequest.farAllocations = sortedAllocations);
      const allocations = nearAllocations.concat(farAllocations);

      if (allocations.length > 1) {
        order.multiAllocation = true;
        order.accountCode = this.getMultiAccountLabel(allocations);
        order.productTooltip = this.createProductTooltip(order, allocations);

        switch (orderRequest.product) {
          case CubiqTS.Product.SWP:
            this.setupSwapProperties(order, orderRequest);
            //`${orderRequest.fixedAmount} / ${orderRequest.swapFixedAmount}`;
            break;
          case CubiqTS.Product.SSP:
            break;
        }
      } else if (allocations.length == 1) {
        // For single allocation BMs the server may not populate the single value date on the allocation as it's
        // defined at the top level
        const primeAllocation = allocations[0];
        if (!primeAllocation.settleDate && orderRequest.valueDate !== undefined) {
          primeAllocation.settleDate = orderRequest.valueDate;
        }
        if (!primeAllocation.settleTenor && orderRequest.tenor !== undefined) {
          primeAllocation.settleTenor = orderRequest.tenor;
        }
      }
    }
  }

  /**
   * Ensure all allocations are sorted in Value date order then by account
   * @param allocations
   * @returns
   */
  private sortAllocations(
    allocations: CubiqTS.AllocationRequestData[],
  ): CubiqTS.AllocationRequestData[] {
    if (!Array.isArray(allocations)) {
      return [];
    }
    if (allocations.length < 2) {
      return allocations;
    }

    return allocations.sort((alloc1, alloc2) => {
      const d1 = alloc1.settleDate;
      const d2 = alloc2.settleDate;
      if (d1 && d2) {
        if (d1.year == d2.year) {
          if (d1.month == d2.month) {
            if (d1.day == d2.day) {
              const acc1 = alloc1.accountCode ?? "";
              const acc2 = alloc2.accountCode ?? "";
              return acc1.localeCompare(acc2);
            }
            return d1.day - d2.day;
          }
          return d1.month - d2.month;
        }
        return d1.year - d2.year;
      }
      return d1 ? 1 : d2 ? -1 : 0;
    });
  }

  /**
   * Determine label for accounts column for multi-allocation benchmarks
   * @param allocations
   * @returns
   */
  private getMultiAccountLabel(allocations: CubiqTS.AllocationRequestData[]): string {
    let isSingleAccount: boolean = true;
    const allocation = allocations[0];
    let firstAccountCode: string = allocation.accountCode;
    // Check all other allocations to see if they have the same account
    for (let i = 1; i < allocations.length; i++) {
      if (allocations[i].accountCode !== firstAccountCode) {
        isSingleAccount = false;
        break;
      }
    }
    if (isSingleAccount) {
      return firstAccountCode;
    }
    return "MULTIPLE";
  }

  /**
   * Update the Swap-related properties of the order from the request. This will set up
   * the correct near/far amounts and value dates
   * @param order
   * @param orderRequest
   */
  private setupSwapProperties(
    order: ScratchPadOrder,
    orderRequest: CubiqTS.FixingOrderRequestData,
  ) {
    order.fixedAmount = this.orderAmountHelper.formatAmount(
      order.fixedCcy,
      new CubiqTS.BigNumber(order.fixedAmount),
      true,
      new CubiqTS.BigNumber(orderRequest.fixedAmount),
      new CubiqTS.BigNumber(orderRequest.swapFixedAmount),
      false,
    );
    const nearDateStr = `${orderRequest.valueDate.year}-${orderRequest.valueDate.month}-${orderRequest.valueDate.day}`;
    const nearDate = CubiqTS.moment(nearDateStr, "YYYY-MM-DD");
    const farDateStr = `${orderRequest.swapValueDate.year}-${orderRequest.swapValueDate.month}-${orderRequest.swapValueDate.day}`;
    const farDate = CubiqTS.moment(farDateStr, "YYYY-MM-DD");
    order.swapValueDate = this.valueDateHelper.formatSwapValueDate(
      orderRequest.tenor,
      nearDate,
      orderRequest.swapTenor,
      farDate,
    );
  }

  /**
   * For multi-allocation benchmarks, list all the allocations inside the order.
   * @param order
   * @param allocations
   * @returns
   */
  private createProductTooltip(
    order: ScratchPadOrder,
    allocations: CubiqTS.AllocationRequestData[],
  ): string {
    if (allocations?.length > 1) {
      const rows: string[] = ["Allocations:"];
      allocations.forEach((allocation) => {
        const intentStr = allocation.allocationIntent == CubiqTS.OrderIntent.BUY ? "BUY" : "SELL";
        const amountStr = this.orderAmountHelper.formatAmount(
          order.fixedCcy,
          new CubiqTS.BigNumber(allocation.allocationQty),
          false,
          null,
          null,
        );
        let valueDateStr: string;
        if (allocation.settleDate instanceof CubiqTS.StandardDateImpl) {
          const settleDate = CubiqTS.moment.tz(
            [
              allocation.settleDate.year,
              allocation.settleDate.month - 1,
              allocation.settleDate.day,
            ],
            "UTC",
          );
          valueDateStr = this.valueDateHelper.formatValueDate(settleDate, allocation.settleTenor);
        } else {
          valueDateStr = this.valueDateHelper.getTenorStr(allocation.settleTenor);
        }

        rows.push(`${intentStr} ${amountStr} ${valueDateStr} Account: ${allocation.accountCode}`);
      });
      // Make sure our tooltip is less than the max size
      const fullText = rows.join("\n");
      if (fullText.length > MAX_TOOLTIP_SIZE) {
        return fullText.substring(1, MAX_TOOLTIP_SIZE) + "...";
      }
      return fullText;
    }
    return "";
  }
}
